import { Tooltip } from '@mui/material';
import {
  selectBundleTabPage,
  selectPageRefWrappingStyle,
  selectParams,
  selectCurrentSelectedFileMetaData,
  selectFileToCompare,
  selectDocuments,
  selectQuery,
} from 'common/selectors';
import { createPageRef } from 'features/case/redux/createPageRef';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import AutosizeInput from 'react-input-autosize';
import onClickOutside from 'react-onclickoutside';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getGlobalPageText, getStartPageForGlobalPaging } from './globalPage';

const CurrentGlobalPage = ({
  currentFileMetaData,
  currentPageNumber,
  setPageNumber,
  fileId,
  startPageForGlobalPaging,
  readOnly,
  isFileToCompare,
}: {
  currentFileMetaData: any;
  currentPageNumber: number;
  setPageNumber: (val: any) => void;
  fileId: string;
  startPageForGlobalPaging: string;
  readOnly?: boolean;
  isFileToCompare?: boolean;
}) => {
  const dispatch = useDispatch();
  const { case: aCase, file } = useSelector(selectParams);
  const isBundleTabPage = useSelector(selectBundleTabPage);
  const pageRefWrappingStyle = useSelector(selectPageRefWrappingStyle);
  const currentSelectedFile = useSelector(selectCurrentSelectedFileMetaData);
  const fileToCompare = useSelector(selectFileToCompare);
  const documents = useSelector(selectDocuments);
  const query = useSelector(selectQuery) as any;
  const location = useLocation();
  const [gPNumberValue, setGPNumberValue] = useState<number>(0);
  const [openTooltip, setOpenTooltip] = useState(false);
  const gPNumberElement = useRef<any>(null);
  const hasMetaFromProps = !!(currentFileMetaData && Object.keys(currentFileMetaData).length > 0);
  const safeMeta = hasMetaFromProps ? currentFileMetaData : {};

  // Use the correct Redux state based on whether this is the compare file or main file
  const reduxFileData = isFileToCompare ? fileToCompare : currentSelectedFile;

  // For compare file, prioritize Redux state over safeMeta to ensure correct startPage
  const startPage = isFileToCompare
    ? startPageForGlobalPaging || reduxFileData?.startPage || safeMeta.startPage
    : startPageForGlobalPaging || safeMeta.startPage || reduxFileData?.startPage;

  // For compare file, use Redux data for all metadata to ensure consistency
  const effectiveMeta =
    isFileToCompare && reduxFileData
      ? {
          ...safeMeta,
          startPage: reduxFileData.startPage || safeMeta.startPage,
          globalPagePrefix: reduxFileData.globalPagePrefix || safeMeta.globalPagePrefix,
          tab: reduxFileData.tab || safeMeta.tab,
          pageCount: reduxFileData.pageCount || safeMeta.pageCount,
        }
      : safeMeta;
  const focusedParam = query?.focused;
  const focusedId = Array.isArray(focusedParam)
    ? focusedParam.length > 0 && focusedParam[0] != null && focusedParam[0] !== ''
      ? String(focusedParam[0])
      : null
    : typeof focusedParam === 'string' || typeof focusedParam === 'number'
      ? String(focusedParam)
      : null;
  const fallbackDoc = useMemo(() => {
    const targetId = fileId || file || focusedId;
    if (!targetId || !Array.isArray(documents) || documents.length === 0) return null;
    const targetValue = String(targetId);
    return (
      documents.find((doc: any) => {
        const candidates = [doc?.id, doc?.fileId, doc?.folderFileId, doc?.compositeKey];
        return candidates.some((value) => value != null && String(value) === targetValue);
      }) || null
    );
  }, [documents, fileId, file, focusedId]);
  const fallbackBundleLoc = (fallbackDoc?.bundleLocations && fallbackDoc.bundleLocations[0]) || {};
  const fallbackMeta =
    fallbackDoc || Object.keys(fallbackBundleLoc).length > 0
      ? { ...(fallbackDoc || {}), ...fallbackBundleLoc }
      : {};
  const effectiveMetaWithFallback = useMemo(
    () => ({
      ...fallbackMeta,
      ...effectiveMeta,
      globalPagePrefix: effectiveMeta?.globalPagePrefix || fallbackMeta?.globalPagePrefix,
      tab: effectiveMeta?.tab || fallbackMeta?.tab,
      startPage: effectiveMeta?.startPage ?? fallbackMeta?.startPage,
      pageCount: effectiveMeta?.pageCount ?? fallbackMeta?.pageCount,
    }),
    [effectiveMeta, fallbackMeta],
  );
  const hasMetadata = hasMetaFromProps || Object.keys(fallbackMeta).length > 0;
  const urlOverrides = useMemo(() => {
    const currentPath = location.pathname || '';
    const searchParams = new URLSearchParams(location.search || '');
    const folderIdFromSearch = searchParams.get('folderId');
    const isBigViewScreen =
      !!folderIdFromSearch &&
      folderIdFromSearch !== 'recordings' &&
      /^\/case\/[^/]+\/files\/[^/]+$/.test(currentPath) &&
      searchParams.get('view') !== 'share';
    if (!isBigViewScreen) return null;
    const overrides: Record<string, string> = {};
    const globalPagePrefix = searchParams.get('globalPagePrefix');
    const tab = searchParams.get('tab');
    const urlStartPage = searchParams.get('startPage');
    const urlPageCount = searchParams.get('pageCount');
    if (globalPagePrefix) overrides.globalPagePrefix = globalPagePrefix;
    if (tab) overrides.tab = tab;
    if (urlStartPage) overrides.startPage = urlStartPage;
    if (urlPageCount) overrides.pageCount = urlPageCount;
    return Object.keys(overrides).length > 0 ? overrides : null;
  }, [location.search, location.pathname]);
  const resolvedMeta = urlOverrides
    ? { ...effectiveMetaWithFallback, ...urlOverrides }
    : effectiveMetaWithFallback;

  const resolvedStartPage = urlOverrides?.startPage || startPage || resolvedMeta.startPage || '';
  const startPageVal = (resolvedStartPage || 0).toString().split('.');
  const isStartPageFirstDigitAfterDecimalZero =
    startPageVal && startPageVal.length > 1 && startPageVal[1].startsWith('0');
  const currentFileStartPage = getStartPageForGlobalPaging(resolvedStartPage);
  const pageCount = Number(resolvedMeta.pageCount) || 0;
  const endGPNumber = pageCount + currentFileStartPage - 1;

  const gpText = hasMetadata
    ? getGlobalPageText(startPageForGlobalPaging, resolvedMeta, gPNumberValue)
    : '';

  //@ts-expect-error because of the way react-onclickoutside is typed
  CurrentGlobalPage.handleClickOutside = () =>
    gPNumberElement.current && gPNumberElement.current.blur();
  const changePageNumber = () => {
    if (isBundleTabPage) {
      setPageNumber(gPNumberValue);
      setGPNumberValue(gPNumberValue);
    } else {
      const newNum = Math.min(Math.max(gPNumberValue, currentFileStartPage), endGPNumber);
      const localPageValue = newNum - currentFileStartPage + 1;
      setPageNumber(localPageValue);
      setGPNumberValue(newNum);
    }
  };
  const copyToClipboard = () => {
    let text = isBundleTabPage
      ? `${resolvedMeta.globalPagePrefix}/${resolvedMeta.tab}/${currentPageNumber}`
      : gpText.toString();
    if (!pageRefWrappingStyle || pageRefWrappingStyle === 'braces') {
      text = `{${text}}`;
    } else if (pageRefWrappingStyle === 'brackets') {
      text = `[${text}]`;
    }
    const fileIdentifier = fileId || file || focusedId || fallbackDoc?.id || fallbackDoc?.fileId;
    if (!fileIdentifier) return;
    const url =
      `${window.location.origin}/case/${aCase}/files/${fileIdentifier}?goToPage=${currentPageNumber}` +
      `${resolvedMeta.tab ? `&tab=${resolvedMeta.tab}` : ''}` +
      `${resolvedStartPage ? `&startPage=${resolvedStartPage}` : ''}` +
      `${resolvedMeta.pageCount ? `&pageCount=${resolvedMeta.pageCount}` : ''}` +
      `${resolvedMeta.globalPagePrefix ? `&globalPagePrefix=${resolvedMeta.globalPagePrefix}` : ''}`;
    const htmlToCopy = `<a href="${url}" style="text-decoration: none; color: inherit;">${text}</a>`;
    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ])
      .then(() => {
        handleTooltipOpen();
        dispatch(createPageRef({ globalPage: text }));
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleTooltipClose = () => setOpenTooltip(false);
  const handleTooltipOpen = () => setOpenTooltip(true);

  useEffect(() => {
    if (openTooltip) {
      setTimeout(() => {
        setOpenTooltip(false);
      }, 1500);
    }
  }, [openTooltip]);

  useEffect(() => {
    if (isBundleTabPage) {
      setGPNumberValue(currentPageNumber);
    } else {
      const currentGPNumber = currentFileStartPage + currentPageNumber - 1;
      setGPNumberValue(currentGPNumber);
    }
  }, [currentPageNumber, currentFileStartPage, isBundleTabPage]);

  useEffect(() => {
    if (!gPNumberElement.current) return;

    gPNumberElement.current.input.readOnly = !!readOnly;
  }, [readOnly]);

  if (!hasMetadata) {
    return null;
  }

  return (
    <Tooltip
      placement="top"
      onClose={handleTooltipClose}
      open={openTooltip}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={`${gpText} - Copied to clipboard`}
      arrow
      PopperProps={{ style: { zIndex: 100007 } }}
    >
      <div
        className="pageNumber"
        onClick={() => gPNumberElement.current && gPNumberElement.current.focus()}
        onDoubleClick={(e: any) => {
          e.stopPropagation();
          copyToClipboard();
        }}
      >
        <div style={{ marginLeft: '0.3rem' }}>
          {isBundleTabPage ? (
            <>
              <span style={{ marginRight: '0.1rem' }}>
                {resolvedMeta.globalPagePrefix}
                {' / '}
                {resolvedMeta.tab}
                {' / '}
              </span>
              <AutosizeInput
                ref={(el: any) => {
                  gPNumberElement.current = el;
                  if (!el) return;
                  el.input.readOnly = !!readOnly;
                }}
                value={gPNumberValue || ''}
                inputStyle={{ textAlign: 'left', border: 'none', background: 'transparent' }}
                onChange={(event: any) => {
                  const re = /^[0-9\b]+$/;
                  if (event.target.value === '' || re.test(event.target.value)) {
                    setGPNumberValue(event.target.value);
                  }
                }}
                onBlur={changePageNumber}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    gPNumberElement.current.blur();
                  }
                  if ((e.ctrlKey || e.metaKey) && e.keyCode === 67) {
                    copyToClipboard();
                  }
                }}
                autoComplete="off"
              />
            </>
          ) : (
            <>
              <span style={{ marginRight: '-0.1rem' }}>
                {resolvedMeta.globalPagePrefix}
                {startPageVal.length > 1 &&
                  startPageVal[0] + `.${isStartPageFirstDigitAfterDecimalZero ? '0' : ''}`}
              </span>
              <AutosizeInput
                ref={gPNumberElement}
                value={gPNumberValue || ''}
                onChange={(event: any) => {
                  const re = /^[0-9\b]+$/;
                  if (event.target.value === '' || re.test(event.target.value))
                    setGPNumberValue(event.target.value);
                }}
                onBlur={changePageNumber}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') gPNumberElement.current.blur();
                  if ((e.ctrlKey || e.metaKey) && e.keyCode === 67) copyToClipboard();
                }}
                autoComplete="off"
              />
            </>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

export default onClickOutside(CurrentGlobalPage, {
  //@ts-expect-error because of the way react-onclickoutside is typed
  handleClickOutside: () => CurrentGlobalPage.handleClickOutside,
});
